import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Hammer, Home, Wrench, CalendarPlus } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1659930087003-2d64e33181f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxhcnRpc2FuJTIwd29vZHdvcmtpbmclMjBjcmFmdHNtYW5zaGlwfGVufDB8fHx8MTc4Mjg5MzU2N3ww&ixlib=rb-4.1.0&q=85";
const STAIRS_1 = "https://images.unsplash.com/photo-1580911498851-4999ad5327b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHx3b29kZW4lMjBzdGFpcmNhc2UlMjBtb2Rlcm58ZW58MHx8fHwxNzgzODMwOTE5fDA&ixlib=rb-4.1.0&q=85";
const STAIRS_2 = "https://images.unsplash.com/photo-1591638212063-c2f69701a931?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHx3b29kZW4lMjBzdGFpcmNhc2UlMjBtb2Rlcm58ZW58MHx8fHwxNzgzODMwOTE5fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="bg-craft">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-16 md:pb-24">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 stagger-in">
            <div className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-primary bg-primary/5 border border-primary/20 rounded-full px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-primary"></span> dal 1950 · artigiani italiani
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] text-foreground">
              Scale, ringhiere e infissi<br/>
              <span className="text-primary italic">costruiti a mano</span>, prenotati online.
            </h1>
            <p className="text-base md:text-lg text-foreground/70 mt-6 max-w-xl">
              Fissa un sopralluogo in cantiere, vieni a trovarci in show room o richiedi un intervento di riparazione.
              La nostra squadra ti risponde entro 24 ore.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full h-12 px-6" data-testid="hero-cta-book">
                <Link to="/prenota"><CalendarPlus className="h-4 w-4 mr-2" /> Prenota ora</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-6 border-foreground/20">
                <a href="#servizi">Scopri i servizi <ArrowRight className="h-4 w-4 ml-2" /></a>
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <div><div className="font-display text-3xl text-foreground">40+</div><div className="text-xs text-foreground/60 uppercase tracking-widest">anni di mestiere</div></div>
              <div><div className="font-display text-3xl text-foreground">1.2k</div><div className="text-xs text-foreground/60 uppercase tracking-widest">progetti</div></div>
              <div><div className="font-display text-3xl text-foreground">24h</div><div className="text-xs text-foreground/60 uppercase tracking-widest">risposta</div></div>
            </div>
          </div>
          <div className="md:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-sm hero-grain">
              <img src={HERO} alt="Artigiano al lavoro" className="w-full h-[420px] md:h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-white border border-border rounded-xl p-4 shadow-sm w-56">
              <div className="text-xs uppercase tracking-widest text-foreground/50">Show room</div>
              <div className="font-display text-lg mt-1">Vieni a toccare i materiali</div>
              <div className="text-xs text-foreground/60 mt-1">Legno · Ferro · Vetro</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVIZI */}
      <section id="servizi" className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs tracking-widest uppercase text-primary">Cosa puoi prenotare</div>
            <h2 className="font-display text-3xl md:text-4xl mt-2 max-w-xl">Tre servizi, un solo modulo. Il resto lo facciamo noi.</h2>
          </div>
          <Button asChild variant="outline" className="rounded-full self-start md:self-auto">
            <Link to="/prenota">Vai al modulo <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </div>

        <div className="grid md:grid-cols-12 gap-5">
          <ServiceCard
            className="md:col-span-5"
            icon={<Home className="h-5 w-5" />}
            title="Sopralluogo in cantiere"
            desc="Un nostro tecnico viene sul posto, prende misure e valuta la fattibilità del progetto."
            tag="Gratuito"
            image={STAIRS_1}
            testId="service-card-sopralluogo"
          />
          <ServiceCard
            className="md:col-span-4"
            icon={<Hammer className="h-5 w-5" />}
            title="Appuntamento in show room"
            desc="Vieni a vedere materiali, finiture e progetti realizzati insieme al nostro consulente."
            tag="Su appuntamento"
            testId="service-card-showroom"
          />
          <ServiceCard
            className="md:col-span-3"
            icon={<Wrench className="h-5 w-5" />}
            title="Riparazione"
            desc="Scale, ringhiere e infissi. Descrivi il problema, ti confermiamo l'intervento."
            tag="Su approvazione"
            image={STAIRS_2}
            testId="service-card-riparazione"
          />
        </div>

        <div className="mt-8 flex items-start gap-3 bg-[#FFF4E5] border-l-4 border-[#F5A623] text-[#7A5211] rounded-md px-5 py-4">
          <div className="font-semibold">Nota per le riparazioni:</div>
          <div className="text-sm">In caso di prenotazione di una riparazione è previsto un costo di partenza e chiamata di <b>60€</b>, da aggiungere poi all'eventuale lavoro.</div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20 border-t border-border">
        <div className="text-xs tracking-widest uppercase text-primary">Come funziona</div>
        <h2 className="font-display text-3xl md:text-4xl mt-2 max-w-xl">In tre passaggi, senza telefonate.</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-10 stagger-in">
          {[
            { n: "01", t: "Scegli il servizio", d: "Sopralluogo, show room o riparazione." },
            { n: "02", t: "Fissa data e ora", d: "Seleziona lo slot che preferisci dal calendario." },
            { n: "03", t: "Ricevi conferma", d: "Ti scriviamo via email. Per le riparazioni previa approvazione." },
          ].map((s) => (
            <div key={s.n} className="bg-white border border-border rounded-xl p-6 card-lift">
              <div className="font-mono text-xs text-primary">{s.n}</div>
              <div className="font-display text-2xl mt-2">{s.t}</div>
              <div className="text-sm text-foreground/70 mt-2">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="rounded-2xl bg-foreground text-background p-8 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-xs tracking-widest uppercase text-primary">Pronto?</div>
            <h3 className="font-display text-3xl md:text-4xl mt-2 max-w-xl text-background">Fissa il tuo appuntamento in due minuti.</h3>
          </div>
          <Button asChild size="lg" className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:bg-[#256696]">
            <Link to="/prenota">Prenota ora <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ className, icon, title, desc, tag, image, testId }) {
  return (
    <div className={`bg-white border border-border rounded-xl overflow-hidden card-lift ${className || ""}`} data-testid={testId}>
      {image && (
        <div className="h-40 w-full overflow-hidden">
          <img src={image} alt={title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">{icon}</div>
          <div className="text-[10px] tracking-widest uppercase bg-secondary text-foreground/70 rounded-full px-2 py-1">{tag}</div>
        </div>
        <div className="font-display text-xl mt-4">{title}</div>
        <div className="text-sm text-foreground/70 mt-2">{desc}</div>
      </div>
    </div>
  );
}
